import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Select, 
  Checkbox, 
  message, 
  Space, 
  Tag,
  Tooltip,
  Switch,
  Divider,
  Alert,
  Popconfirm,
  Spin,
  Typography
} from 'antd';
import { 
  EditOutlined, 
  UserOutlined, 
  SafetyCertificateOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Role, User, PermissionMatrix, ModulePermission, UserWithRole } from '../../../../shared/types';
import permissionService from '../../services/permissionService';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { Text } = Typography;

interface Module {
  ModuleKey: string;
  ModuleName: string;
}

const PermissionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [modules, setModules] = useState<Module[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithRole | null>(null);
  const [rolePermissions, setRolePermissions] = useState<PermissionMatrix[]>([]);
  const [userPermissions, setUserPermissions] = useState<PermissionMatrix[]>([]);
  
  const [form] = Form.useForm();
  const [authError, setAuthError] = useState<string>('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    if (!checkAuth()) return;
    
    setLoading(true);
    try {
      const [modulesData, rolesData, usersData] = await Promise.all([
        permissionService.getModules(),
        permissionService.getRoles(),
        permissionService.getUsersWithRoles()
      ]);

      setModules(modulesData);
      setRoles(rolesData);
      setUsers(usersData);
      
      message.success('Tải dữ liệu thành công');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRolePermissions = async (role: Role) => {
    setSelectedRole(role);
    setLoading(true);
    
    try {
      const permissions = await permissionService.getRolePermissions(role.RoleId);
      setRolePermissions(permissions);
      setRoleModalVisible(true);
    } catch (error) {
      console.error('Error loading role permissions:', error);
      message.error('Không thể tải quyền của vai trò');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUserPermissions = async (user: UserWithRole) => {
    setSelectedUser(user);
    setLoading(true);
    
    try {
      const permissions = await permissionService.getUserPermissions(user.UserId);
      console.log('User permissions loaded:', permissions); // Debug log
      setUserPermissions(permissions || []); // Ensure we have an array
      setUserModalVisible(true);
    } catch (error) {
      console.error('Error loading user permissions:', error);
      message.error('Không thể tải quyền của người dùng');
      setUserPermissions([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRolePermissions = async () => {
    if (!selectedRole) return;

    try {
      const permissionsToSave: ModulePermission[] = rolePermissions.map(perm => ({
        moduleKey: perm.ModuleKey,
        view: perm.CanView,
        add: perm.CanAdd,
        edit: perm.CanEdit,
        delete: perm.CanDelete,
        all: perm.CanAll
      }));

      await permissionService.updateRolePermissions(selectedRole.RoleId, permissionsToSave);
      
      message.success('Cập nhật quyền vai trò thành công');
      setRoleModalVisible(false);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error saving role permissions:', error);
      message.error('Lỗi khi lưu quyền vai trò');
    }
  };

  const handleSaveUserPermissions = async () => {
    if (!selectedUser) return;

    try {
      const permissionsToSave: ModulePermission[] = userPermissions.map(perm => ({
        moduleKey: perm.ModuleKey,
        view: perm.CanView,
        add: perm.CanAdd,
        edit: perm.CanEdit,
        delete: perm.CanDelete,
        all: perm.CanAll
      }));

      await permissionService.updateUserPermissions(selectedUser.UserId, permissionsToSave);
      
      message.success('Cập nhật quyền người dùng thành công');
      setUserModalVisible(false);
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error saving user permissions:', error);
      message.error('Lỗi khi lưu quyền người dùng');
    }
  };

  const handleResetUserPermissions = async (userId: number) => {
    try {
      await permissionService.resetUserPermissions(userId);
      message.success('Reset quyền người dùng thành công');
      loadInitialData(); // Refresh data
    } catch (error) {
      console.error('Error resetting user permissions:', error);
      message.error('Lỗi khi reset quyền người dùng');
    }
  };

  const updateRolePermission = (moduleKey: string, permission: string, value: boolean) => {
    setRolePermissions(prevPermissions => prevPermissions.map(perm => {
      if (perm.ModuleKey === moduleKey) {
        const updated = { ...perm };
        
        if (permission === 'all') {
          updated.CanAll = value;
          if (value) {
            updated.CanView = true;
            updated.CanAdd = true;
            updated.CanEdit = true;
            updated.CanDelete = true;
          }
        } else {
          (updated as any)[`Can${permission.charAt(0).toUpperCase() + permission.slice(1)}`] = value;
          if (!value) {
            updated.CanAll = false;
          }
        }
        
        return updated;
      }
      return perm;
    }));
  };

  const updateUserPermission = (moduleKey: string, permission: string, value: boolean) => {
    setUserPermissions(prevPermissions => prevPermissions.map(perm => {
      if (perm.ModuleKey === moduleKey) {
        const updated = { ...perm };
        
        if (permission === 'all') {
          updated.CanAll = value;
          if (value) {
            updated.CanView = true;
            updated.CanAdd = true;
            updated.CanEdit = true;
            updated.CanDelete = true;
          }
        } else {
          (updated as any)[`Can${permission.charAt(0).toUpperCase() + permission.slice(1)}`] = value;
          if (!value) {
            updated.CanAll = false;
          }
        }
        
        // Mark as having custom permission if different from role
        const isCustom = updated.CanView !== updated.RoleCanView ||
                        updated.CanAdd !== updated.RoleCanAdd ||
                        updated.CanEdit !== updated.RoleCanEdit ||
                        updated.CanDelete !== updated.RoleCanDelete ||
                        updated.CanAll !== updated.RoleCanAll;
        
        updated.HasCustomPermission = isCustom;
        
        return updated;
      }
      return perm;
    }));
  };

  // Columns cho bảng roles
  const roleColumns: ColumnsType<Role> = [
    {
      title: 'Tên vai trò',
      dataIndex: 'RoleName',
      key: 'RoleName',
      render: (text, record) => (
        <Space>
          <Tag color={record.RoleKey === 'admin' ? 'red' : 'blue'}>{text}</Tag>
        </Space>
      )
    },
    {
      title: 'Key',
      dataIndex: 'RoleKey',
      key: 'RoleKey',
      render: text => <code>{text}</code>
    },

    {
      title: 'Hành động',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<EditOutlined />}
          onClick={() => handleEditRolePermissions(record)}
        >
          Phân quyền
        </Button>
      )
    }
  ];

  // Columns cho bảng users
  const userColumns: ColumnsType<UserWithRole> = [
    {
      title: 'Tên đăng nhập',
      dataIndex: 'Username',
      key: 'Username',
      render: text => <strong>{text}</strong>,
      sorter: (a, b) => a.Username.localeCompare(b.Username),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <input
            placeholder="Tìm tên đăng nhập"
            value={selectedKeys[0] as string}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onKeyDown={e => { if (e.key === 'Enter') confirm(); }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} icon={<SearchOutlined />}>Lọc</Button>
            <Button size="small" onClick={() => clearFilters && clearFilters()}>Reset</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.Username.toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Họ tên',
      dataIndex: 'FullName',
      key: 'FullName',
      sorter: (a, b) => (a.FullName || '').localeCompare(b.FullName || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <input
            placeholder="Tìm họ tên"
            value={selectedKeys[0] as string}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onKeyDown={e => { if (e.key === 'Enter') confirm(); }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} icon={<SearchOutlined />}>Lọc</Button>
            <Button size="small" onClick={() => clearFilters && clearFilters()}>Reset</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => (record.FullName || '').toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'Email',
      sorter: (a, b) => (a.Email || '').localeCompare(b.Email || ''),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <input
            placeholder="Tìm email"
            value={selectedKeys[0] as string}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onKeyDown={e => { if (e.key === 'Enter') confirm(); }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()} icon={<SearchOutlined />}>Lọc</Button>
            <Button size="small" onClick={() => clearFilters && clearFilters()}>Reset</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => (record.Email || '').toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Vai trò',
      dataIndex: 'RoleName',
      key: 'RoleName',
      sorter: (a, b) => (a.RoleName || '').localeCompare(b.RoleName || ''),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Sinh viên', value: 'Sinh viên' },
        { text: 'Giáo viên', value: 'Giáo viên' },
        { text: 'Nhân viên', value: 'Nhân viên' },
      ],
      onFilter: (value, record) => (record.RoleName || '').toLowerCase().includes((value as string).toLowerCase()),
      render: (text, record) => (
        <Tag color={record.RoleKey === 'admin' ? 'red' : 'blue'}>{text}</Tag>
      )
    },
    {
      title: 'Hành động',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => handleEditUserPermissions(record)}
          >
            Phân quyền riêng
          </Button>
          <Popconfirm
            title="Reset quyền người dùng"
            description="Bạn có chắc chắn muốn reset quyền của người dùng này về mặc định (theo vai trò)?"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleResetUserPermissions(record.UserId)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <Button 
              danger
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const PermissionMatrix: React.FC<{
    permissions: PermissionMatrix[];
    onUpdate: (moduleKey: string, permission: string, value: boolean) => void;
    showRoleComparison?: boolean;
  }> = ({ permissions, onUpdate, showRoleComparison = false }) => {
    
    // Safety check for permissions data
    if (!permissions || !Array.isArray(permissions)) {
      return (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Alert
            message="Không có dữ liệu quyền"
            description="Không thể tải dữ liệu quyền cho người dùng này"
            type="warning"
            showIcon
          />
        </div>
      );
    }

    if (permissions.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Alert
            message="Chưa có quyền nào"
            description="Người dùng chưa được phân quyền cho module nào"
            type="info"
            showIcon
          />
        </div>
      );
    }

    return (
      <div style={{ marginTop: 16 }}>
        <Table
          dataSource={permissions}
          rowKey="ModuleKey"
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
        >
        <Table.Column 
          title="Module" 
          dataIndex="ModuleName" 
          key="ModuleName"
          width={200}
          render={(text, record: PermissionMatrix) => (
            <div>
              <strong>{text || record?.ModuleKey || 'Unknown Module'}</strong>
              {showRoleComparison && record?.HasCustomPermission && (
                <Tag color="orange" style={{ marginLeft: 8, fontSize: 'smaller' }}>
                  Custom
                </Tag>
              )}
            </div>
          )}
        />
        
        {showRoleComparison && (
          <>
            <Table.Column
              title="Quyền từ Role"
              key="rolePermissions"
              width={150}
              render={(_, record: PermissionMatrix) => (
                <Space size="small">
                  {record?.RoleCanView && <Tag style={{ fontSize: 'smaller' }}>View</Tag>}
                  {record?.RoleCanAdd && <Tag style={{ fontSize: 'smaller' }}>Add</Tag>}
                  {record?.RoleCanEdit && <Tag style={{ fontSize: 'smaller' }}>Edit</Tag>}
                  {record?.RoleCanDelete && <Tag style={{ fontSize: 'smaller' }}>Delete</Tag>}
                  {record?.RoleCanAll && <Tag color="red" style={{ fontSize: 'smaller' }}>All</Tag>}
                </Space>
              )}
            />
          </>
        )}

        <Table.Column
          title="View"
          key="view"
          width={80}
          render={(_, record: PermissionMatrix) => (
            <Checkbox
              checked={record?.CanView || false}
              onChange={(e) => record?.ModuleKey && onUpdate(record.ModuleKey, 'view', e.target.checked)}
            />
          )}
        />
        
        <Table.Column
          title="Add"
          key="add"
          width={80}
          render={(_, record: PermissionMatrix) => (
            <Checkbox
              checked={record?.CanAdd || false}
              onChange={(e) => record?.ModuleKey && onUpdate(record.ModuleKey, 'add', e.target.checked)}
            />
          )}
        />
        
        <Table.Column
          title="Edit"
          key="edit"
          width={80}
          render={(_, record: PermissionMatrix) => (
            <Checkbox
              checked={record?.CanEdit || false}
              onChange={(e) => record?.ModuleKey && onUpdate(record.ModuleKey, 'edit', e.target.checked)}
            />
          )}
        />
        
        <Table.Column
          title="Delete"
          key="delete"
          width={80}
          render={(_, record: PermissionMatrix) => (
            <Checkbox
              checked={record?.CanDelete || false}
              onChange={(e) => record?.ModuleKey && onUpdate(record.ModuleKey, 'delete', e.target.checked)}
            />
          )}
        />
        
        <Table.Column
          title="All"
          key="all"
          width={80}
          render={(_, record: PermissionMatrix) => (
            <Checkbox
              checked={record?.CanAll || false}
              onChange={(e) => record?.ModuleKey && onUpdate(record.ModuleKey, 'all', e.target.checked)}
            />
          )}
        />
      </Table>
    </div>
  );
};

  // Check authentication status
  const checkAuth = () => {
    const token = localStorage.getItem('quanly_truonghoc_token');
    const userInfo = localStorage.getItem('quanly_truonghoc_user');
    
    if (!token || !userInfo) {
      setAuthError('Vui lòng đăng nhập để truy cập trang này');
      return false;
    }
    
    setAuthError('');
    return true;
  };

  // Handle authentication errors from API calls
  const handleApiError = (error: any) => {
    console.error('API Error:', error);
    
    if (error.response?.status === 401 || error.response?.status === 403) {
      setAuthError('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      message.error('Phiên đăng nhập đã hết hạn');
      // Optionally redirect to login
      // navigate('/login');
      return;
    }
    
    const errorMessage = error.response?.data?.message || error.message || 'Có lỗi xảy ra';
    message.error(errorMessage);
  };

  // If there's an authentication error, show login prompt
  if (authError) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Yêu cầu đăng nhập"
          description={authError}
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          action={
            <Space>
              <Button 
                size="small" 
                type="primary" 
                onClick={() => navigate('/login')}
              >
                Đăng nhập
              </Button>
              <Button 
                size="small" 
                onClick={() => {
                  setAuthError('');
                  loadInitialData();
                }}
              >
                Thử lại
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" />
        <Text style={{ marginLeft: 16 }}>Đang tải dữ liệu...</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card title="🔐 Quản lý phân quyền hệ thống" extra={
        <Button icon={<ReloadOutlined />} onClick={loadInitialData}>
          Làm mới
        </Button>
      }>
        <Alert
          message="Hệ thống phân quyền 2 cấp"
          description="Quyền người dùng sẽ ghi đè quyền từ vai trò. Admin có tất cả quyền."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Tabs 
          defaultActiveKey="roles"
          items={[
            {
              key: 'roles',
              label: (
                <span>
                  <SafetyCertificateOutlined />
                  Phân quyền theo vai trò
                </span>
              ),
              children: (
                <Table
                  columns={roleColumns}
                  dataSource={roles}
                  rowKey="RoleId"
                  loading={loading}
                />
              )
            },
            {
              key: 'users',
              label: (
                <span>
                  <UserOutlined />
                  Phân quyền riêng người dùng
                </span>
              ),
              children: (
                <Table
                  columns={userColumns}
                  dataSource={users}
                  rowKey="UserId"
                  loading={loading}
                />
              )
            }
          ]}
        />
      </Card>

      {/* Modal phân quyền vai trò */}
      <Modal
        title={`Phân quyền cho vai trò: ${selectedRole?.RoleName}`}
        open={roleModalVisible}
        onCancel={() => setRoleModalVisible(false)}
        width={1000}
        zIndex={1002}
        footer={[
          <Button key="cancel" onClick={() => setRoleModalVisible(false)}>
            Hủy
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveRolePermissions}>
            Lưu thay đổi
          </Button>
        ]}
      >
        {selectedRole && (
          <PermissionMatrix
            permissions={rolePermissions}
            onUpdate={updateRolePermission}
          />
        )}
      </Modal>

      {/* Modal phân quyền người dùng */}
      <Modal
        title={`Phân quyền riêng cho: ${selectedUser?.FullName || 'N/A'} (${selectedUser?.Username || 'N/A'})`}
        open={userModalVisible}
        onCancel={() => setUserModalVisible(false)}
        width={1200}
        zIndex={1002}
        footer={[
          <Button key="reset" danger onClick={() => selectedUser && handleResetUserPermissions(selectedUser.UserId)}>
            Reset về mặc định
          </Button>,
          <Button key="cancel" onClick={() => setUserModalVisible(false)}>
            Hủy
          </Button>,
          <Button key="save" type="primary" onClick={handleSaveUserPermissions}>
            Lưu thay đổi
          </Button>
        ]}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            <Spin size="large" />
            <div style={{ marginTop: 10 }}>Đang tải dữ liệu quyền...</div>
          </div>
        ) : selectedUser ? (
          <>
            <Alert
              message={`Vai trò hiện tại: ${selectedUser.RoleName || 'Không xác định'}`}
              description="Các quyền được đánh dấu 'Custom' sẽ ghi đè quyền từ vai trò"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <PermissionMatrix
              permissions={userPermissions}
              onUpdate={updateUserPermission}
              showRoleComparison={true}
            />
          </>
        ) : (
          <Alert
            message="Không có dữ liệu người dùng"
            description="Vui lòng thử lại"
            type="error"
            showIcon
          />
        )}
      </Modal>
    </div>
  );
};

export default PermissionManagementPage; 